import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  BookOpen,
  Check,
  ChevronLeft,
  ChevronRight,
  Heart,
  Play,
  Square,
  Type
} from 'lucide-react';
import ScreenLayout from './ScreenLayout';
import { getDevotionalHistory } from '../services/dailyContentService';
import { getBibleReaderPath } from '../services/bibleReaderService';
import { playVerse, stopVerse } from '../services/ttsService';
import { getFromLocalStorage, saveToLocalStorage } from '../utils/localStorageHelpers';

const STORAGE_KEY = 'prayer_app_devotionals';
const FAVORITES_KEY = 'prayer_app_favorite_devotionals';

const readStoredArray = (key) => {
  const stored = getFromLocalStorage(key, []);
  return Array.isArray(stored) ? stored : [];
};

const prepareDevotionals = () => {
  const favorites = new Set([
    ...readStoredArray(FAVORITES_KEY),
    ...readStoredArray(STORAGE_KEY).filter((item) => item?.isFavorite).map((item) => item.id)
  ]);
  return getDevotionalHistory().map((devotional) => ({
    ...devotional,
    isFavorite: favorites.has(devotional.id)
  }));
};

const formatReadingDate = (date) => new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  }).format(new Date(`${date}T12:00:00`));

const DailyDevotional = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [devotionals, setDevotionals] = useState(prepareDevotionals);
  const currentIndex = Math.max(
    0,
    devotionals.findIndex((item) => item.readingDate === searchParams.get('date'))
  );
  const [largeText, setLargeText] = useState(false);
  // Listening and the reflect/read follow-ons came from the retired Daily Dose
  // screen; this is now the only "today" surface, so they live here.
  const [speaking, setSpeaking] = useState(false);
  const [speechError, setSpeechError] = useState('');

  const currentDevotional = devotionals[currentIndex];

  useEffect(() => {
    setSpeaking(false);
    setSpeechError('');
    return () => stopVerse();
  }, [currentDevotional.id]);

  const toggleListen = async () => {
    if (speaking) {
      stopVerse();
      setSpeaking(false);
      return;
    }
    setSpeechError('');
    setSpeaking(true);
    try {
      await playVerse({
        text: currentDevotional.scriptureText,
        reference: currentDevotional.scripture,
        onEnd: () => setSpeaking(false),
        onError: () => {
          setSpeaking(false);
          setSpeechError('This reading could not be played aloud.');
        }
      });
    } catch (playError) {
      setSpeaking(false);
      setSpeechError(playError.message || 'This reading could not be played aloud.');
    }
  };

  const selectDevotional = (index) => {
    setSearchParams(index === 0 ? {} : { date: devotionals[index].readingDate });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateDevotional = (direction) => {
    if (direction === 'older' && currentIndex < devotionals.length - 1) {
      selectDevotional(currentIndex + 1);
    }
    if (direction === 'newer' && currentIndex > 0) {
      selectDevotional(currentIndex - 1);
    }
  };

  const toggleFavorite = () => {
    const devotionalId = currentDevotional.id;
    const newFavoriteStatus = !currentDevotional.isFavorite;
    const updatedDevotionals = devotionals.map((devotional) =>
      devotional.id === devotionalId
        ? { ...devotional, isFavorite: newFavoriteStatus }
        : devotional
    );

    setDevotionals(updatedDevotionals);
    // Retain saved content outside the bundled library while updating these
    // readings. Older versions stored favorite flags in this content cache.
    const cached = readStoredArray(STORAGE_KEY).filter((item) => item?.id != null);
    const merged = new Map([...cached, ...updatedDevotionals].map((item) => [item.id, item]));
    saveToLocalStorage(STORAGE_KEY, [...merged.values()]);

    const favorites = [...new Set([
      ...readStoredArray(FAVORITES_KEY),
      ...devotionals.filter((item) => item.isFavorite).map((item) => item.id)
    ])];
    const updatedFavorites = newFavoriteStatus
      ? [...new Set([...favorites, devotionalId])]
      : favorites.filter((id) => id !== devotionalId);
    saveToLocalStorage(FAVORITES_KEY, updatedFavorites);

  };

  const favoriteDevotionals = devotionals.filter((devotional) => devotional.isFavorite);
  const reflectionParagraphs = currentDevotional.reflection
    ? currentDevotional.reflection.split('\n\n')
    : [];

  const reflectionState = {
    reflection: {
      title: `Reflection on: ${currentDevotional.title}`,
      relatedScripture: currentDevotional.scripture,
      sourceLabel: 'Daily devotional',
      sourcePath: `/devotionals?date=${currentDevotional.readingDate}`
    }
  };

  return (
    <ScreenLayout
      title="Daily Devotional"
      className={`detail-devotional${largeText ? ' detail-large-text' : ''}`}
      actions={(
        <>
          <button
            type="button"
            className="module-text-control"
            onClick={() => setLargeText(!largeText)}
            aria-label={largeText ? 'Use standard text size' : 'Use larger text size'}
            aria-pressed={largeText}
          >
            <Type size={18} /> Text
          </button>
          <button
            type="button"
            className="module-text-control"
            onClick={toggleFavorite}
            aria-label={currentDevotional.isFavorite ? 'Saved' : 'Save devotional'}
            aria-pressed={currentDevotional.isFavorite}
          >
            <Heart size={18} fill={currentDevotional.isFavorite ? 'currentColor' : 'none'} />
            {currentDevotional.isFavorite ? 'Saved' : 'Save'}
          </button>
        </>
      )}
    >
      <header className="detail-heading">
        <p className="detail-date">{formatReadingDate(currentDevotional.readingDate)}</p>
        <h1>{currentDevotional.title}</h1>
        <p className="detail-subtitle">{currentDevotional.scripture}</p>
        <p className="detail-subtitle">A little space to read, reflect, and pray.</p>
      </header>

      <nav className="detail-day-nav" aria-label="Devotional dates">
        <button
          type="button"
          onClick={() => navigateDevotional('older')}
          disabled={currentIndex >= devotionals.length - 1}
          aria-label="Read the previous devotional"
        >
          <ChevronLeft size={18} /> Previous
        </button>
        <span>{currentIndex === 0 ? 'Today' : formatReadingDate(currentDevotional.readingDate)}</span>
        <button
          type="button"
          onClick={() => navigateDevotional('newer')}
          disabled={currentIndex <= 0}
          aria-label="Read the next devotional"
        >
          Next <ChevronRight size={18} />
        </button>
      </nav>

      <div className="detail-grid">
        <article className="detail-paper">
          <section id="devotional-read" className="detail-section">
            <h2 className="detail-section-title">Scripture</h2>
            <div className="detail-scripture">
              <blockquote>“{currentDevotional.scriptureText}”</blockquote>
              <cite>{currentDevotional.scripture}</cite>
            </div>
            <div className="detail-actions">
              <button type="button" className="detail-button detail-button-secondary" onClick={toggleListen}>
                {speaking ? <Square size={17} /> : <Play size={17} />}
                {speaking ? 'Stop' : 'Listen'}
              </button>
              <Link className="detail-button detail-button-secondary" to={getBibleReaderPath(currentDevotional.scripture)}>
                <BookOpen size={18} /> Read the full chapter
              </Link>
            </div>
            {speechError ? <p className="detail-status" role="alert">{speechError}</p> : null}
          </section>

          <section id="devotional-reflect" className="detail-section">
            <h2 className="detail-section-title">Reflection</h2>
            {reflectionParagraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
          </section>

          <section id="devotional-practice" className="detail-section">
            <h2 className="detail-section-title">Carry it with you</h2>
            <ol className="detail-practice-list">
              {currentDevotional.application.map((point, index) => (
                <li key={index}><p>{point}</p></li>
              ))}
            </ol>
          </section>

          <section id="devotional-pray" className="detail-section">
            <h2 className="detail-section-title">Prayer</h2>
            <p>{currentDevotional.prayer}</p>
            <div className="detail-actions">
              <Link className="detail-button" to="/practice/journal" state={reflectionState}>
                Reflect in journal <ChevronRight size={18} />
              </Link>
            </div>
          </section>
        </article>

        <aside className="detail-rail">
          <nav className="detail-rail-section detail-contents" aria-label="Devotional sections">
            <h2>This reading</h2>
            <a href="#devotional-read">Scripture</a>
            <a href="#devotional-reflect">Reflection</a>
            <a href="#devotional-practice">Practice</a>
            <a href="#devotional-pray">Prayer</a>
          </nav>

          <section className="detail-rail-section detail-contents">
            <h2>Keep what matters</h2>
            <p>Take one thought into your day.</p>
            <Link to="/practice/journal" state={reflectionState}>Reflect in journal <ChevronRight size={16} /></Link>
          </section>

          <section className="detail-rail-section detail-contents">
            <h2>Saved readings</h2>
            {favoriteDevotionals.length > 0 ? favoriteDevotionals.map((devotional) => (
              <button
                type="button"
                key={devotional.id}
                onClick={() => selectDevotional(devotionals.findIndex((item) => item.id === devotional.id))}
              >
                <Check size={15} /><span>{devotional.title}</span>
              </button>
            )) : (
              <p>You haven’t saved any readings yet. Tap the heart in the toolbar to keep a reading for later.</p>
            )}
          </section>
        </aside>
      </div>
    </ScreenLayout>
  );
};

export default DailyDevotional;
